import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { BadgeCheck, ArrowUpCircle, Gauge } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * v3.9.42 (Blueprint Stage 1) — "My Plan & Usage". Shows the member's current plan
 * per audience (worker/employer/client), what each includes, and live usage against
 * the limits. Uses the existing design system. Enforcement of limits is layered on
 * next; this page makes the entitlements visible.
 */
import { formatPhp } from "../lib/money";
const peso = formatPhp;
const AUD_LABEL: Record<string, string> = { worker: "Worker / Professional", employer: "Employer", client: "Project Client", supplier: "Supplier" };

// Which usage meter + entitlement key pairs to show per audience.
const METERS: Record<string, { label: string; usageKey: string; entKey: string }[]> = {
  worker: [
    { label: "Service listings", usageKey: "serviceListings", entKey: "serviceListings" },
    { label: "Portfolio items", usageKey: "portfolio", entKey: "portfolio" },
  ],
  employer: [
    { label: "Active jobs", usageKey: "activeJobs", entKey: "activeJobs" },
    { label: "Candidate contacts", usageKey: "", entKey: "candidateContacts" },
    { label: "Team seats", usageKey: "", entKey: "seats" },
  ],
  client: [
    { label: "Active projects", usageKey: "projects", entKey: "" },
  ],
};

function limitLabel(v: any) { return v === -1 ? "Unlimited" : v == null ? "—" : String(v); }

function Meter({ label, used, limit }: { label: string; used: number | null; limit: any }) {
  const unlimited = limit === -1;
  const pct = unlimited || limit == null || limit === 0 ? 0 : Math.min(100, Math.round(((used || 0) / limit) * 100));
  const over = !unlimited && limit != null && used != null && used >= limit;
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-mut">{label}</span>
        <span className={`font-semibold ${over ? "text-brand-red" : "text-navy"}`}>{used != null ? used : "—"}{limit != null ? ` / ${limitLabel(limit)}` : ""}</span>
      </div>
      {limit != null && !unlimited && (
        <div className="h-1.5 rounded-full bg-navy-50 overflow-hidden"><div className={`h-full ${over ? "bg-brand-red" : "bg-brand-green"}`} style={{ width: `${pct}%` }} /></div>
      )}
    </div>
  );
}

export default function Billing() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const [data, setData] = useState<{ sections: any[]; usage: any; paidPlansAvailable: boolean } | null>(null);

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    api.myPlan().then(setData).catch(() => setData({ sections: [], usage: {}, paidPlansAvailable: false }));
  }, [user?.id]);

  if (!data) return <div className="container-x py-16 text-center text-mut">Loading your plan…</div>;

  return (
    <div className="container-x py-6">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-extrabold text-navy">My Plan &amp; Usage</h1>
          <p className="text-sm text-mut">Your current plans and how much of each you've used.</p>
        </div>
        {data.paidPlansAvailable && <Link href="/pricing" className="btn-red h-9 px-3 text-sm"><ArrowUpCircle className="w-4 h-4" />View plans</Link>}
      </div>

      {!data.paidPlansAvailable && <p className="text-sm text-mut mb-6">Paid plans are coming soon. Posting is free, and any existing subscription stays on your account.</p>}

      {data.sections.length === 0 ? (
        <div className="card p-8 text-center text-sm text-mut">Enable an activity (Find Work, Hire, or Post a Project) to see plan options.</div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {data.sections.map((s) => {
            const meters = METERS[s.audience] || [];
            return (
              <div key={s.audience} className="card p-5">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <div>
                    <div className="text-xs text-mut">{AUD_LABEL[s.audience] || s.audience}</div>
                    <div className="font-extrabold text-navy flex items-center gap-2">{s.plan?.name || "Free"}{s.isPaid && <BadgeCheck className="w-4 h-4 text-brand-green" />}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-navy">{s.plan?.price ? peso(s.plan.price) + (s.plan.interval ? `/${s.plan.interval === "year" ? "yr" : "mo"}` : "") : "Free"}</div>
                    {s.renewsAt && <div className="text-[11px] text-mut">renews {new Date(s.renewsAt).toLocaleDateString("en-PH", { month: "short", day: "numeric", year: "numeric" })}</div>}
                  </div>
                </div>
                {meters.length > 0 && (
                  <div className="space-y-3 border-t border-line pt-3">
                    <div className="flex items-center gap-1 text-xs font-semibold text-navy"><Gauge className="w-3.5 h-3.5" />Usage</div>
                    {meters.map((m) => (
                      <Meter key={m.label} label={m.label}
                        used={m.usageKey ? (data.usage?.[m.usageKey] ?? 0) : null}
                        limit={data.paidPlansAvailable && m.entKey ? s.entitlements?.[m.entKey] : null} />
                    ))}
                  </div>
                )}
                {data.paidPlansAvailable && !s.isPaid && (
                  <Link href="/pricing" className="btn-outline w-full justify-center mt-4 text-sm">Upgrade</Link>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
